import { forwardRef, useEffect, useState } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { AutomationRuleFormTypes } from "models/formTypes";
import { AutomationRuleEnums } from "models/enums";
//#endregion
//#region  components
import { RuleCreationForm, useAvailableRuleTypesList } from "components/automationRule";
import useCreateFormHelpers from "./useCreateFromHelpers";
import { useAutomationRuleService } from "serviceHooks";
import { AutomaticRuleCreationFormContainerProps } from "models/propTypes/automationRulePropTypes";
import { LoadingWithLogo, useMountedState } from "components/common";
//#endregion
import cssExports from "theme/styles/main.module.scss";

const RuleCreationFormContainer: React.ForwardRefRenderFunction<
  FormHandles,
  AutomaticRuleCreationFormContainerProps
> = (props, ref) => {
  const automationRuleService = useAutomationRuleService();
  const { ruleId, type } = props;
  const { getMounted } = useMountedState();
  const [loading, setLoading] = useState(false);
  const { validationScheme, initialCreateValues } = useCreateFormHelpers();
  const { availableRuleTypes } = useAvailableRuleTypesList();
  const [initialValues, setInitialValues] = useState<AutomationRuleFormTypes.AutomationRuleCreate>(initialCreateValues);

  useEffect(() => {
    if (ruleId && type) {
      loadRule(ruleId, type);
    }
  }, [ruleId, type]);

  const loadRule = (id: string, type: AutomationRuleEnums.AutomationRuleType) => {
    props.onFormValidationChange && props.onFormValidationChange(false);
    setLoading(true);
    if (type === AutomationRuleEnums.AutomationRuleType.ReceiptDraft) {
      automationRuleService
        .getReceiptDraftRule(id)
        .then((rule) => {
          if (getMounted()) {
            setInitialValues(rule);
          }
          props.onFormValidationChange && props.onFormValidationChange(true);
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }
    setLoading(false);
  };

  const handleSubmit = async (values: AutomationRuleFormTypes.AutomationRuleCreate) => {
    switch (values.type) {
      case AutomationRuleEnums.AutomationRuleType.ReceiptDraft:
        return ruleId && type
          ? await automationRuleService.updateReceiptDraftRule(ruleId, values)
          : await automationRuleService.createReceiptDraftRule(values);
      default:
        throw new Error(`Unsupported automation rule type: ${values.type}`);
    }
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <RuleCreationForm
        ref={ref}
        isEditView={!!ruleId && !!type}
        availableRuleTypes={availableRuleTypes}
        defaultValues={initialValues}
        handleSubmit={handleSubmit}
        validationSchema={validationScheme}
      />
    </>
  );
};

export default forwardRef(RuleCreationFormContainer);
